// src/utils/scoringCalculator.js
// Updated: 11/09/2025 08:30 PM EST
// --- THIS IS A NEW FILE ---

/**
 * Reusable function to calculate summary results for a scoring session.
 * @param {object} session - The scoring session object (must have categories).
 * @param {Array} packagesData - An array of package documents.
 * @param {Array} scoresData - An array of all score documents for the session.
 * @returns {Array} - A ranked list of packages with their average scores.
 */
export function calculateResults(session, packagesData, scoresData) {
    // Basic checks for necessary data
    if (!session?.categories || packagesData.length === 0 || scoresData.length === 0) {
        console.warn("calculateResults: Missing data, returning empty array.", { session, packagesData, scoresData });
        return [];
    }

    // Determine total possible points based on rubric type
    let totalPossibleRaw = 0;
    let totalPossibleWeighted = 0;
    session.categories.forEach(cat => {
        if (session.useSimplifiedRubric) {
            totalPossibleRaw += cat.maxPoints || 0;
            totalPossibleWeighted += (cat.maxPoints || 0) * (cat.weight || 1); // Assuming weight defaults to 1 if missing
        } else {
            cat.criteria?.forEach(crit => { // Add null check for criteria
                totalPossibleRaw += crit.maxPoints || 0;
                totalPossibleWeighted += (crit.maxPoints || 0) * (crit.weight || 0); // Assuming weight defaults to 0 if missing
            });
        }
    });

    // Calculate results per package
    const results = packagesData.map(pkg => {
        const packageScores = scoresData.filter(s => s.packageId === pkg.id);
        const numReviewers = packageScores.length;

        if (numReviewers === 0) {
            return {
                id: pkg.id, name: pkg.name, numReviewers: 0,
                avgRawScore: 0, avgWeightedScore: 0,
                totalRawScore: 0, totalWeightedScore: 0,
                totalPossibleRaw, totalPossibleWeighted, // Add possible totals
                scoresByReviewer: []
            };
        }

        let overallTotalRaw = 0;
        let overallTotalWeighted = 0;
        const scoresByReviewer = [];

        packageScores.forEach(scoreDoc => {
            let reviewerRaw = 0;
            let reviewerWeighted = 0;

            session.categories.forEach((cat, catIndex) => {
                // Check if using simplified rubric for this session
                if (session.useSimplifiedRubric) {
                    const scoreKey = `${catIndex}`;
                    const scoreValue = Number(scoreDoc.scores?.[scoreKey] || 0);
                    reviewerRaw += scoreValue;
                    // Use category weight (default to 1 if missing)
                    reviewerWeighted += scoreValue * (cat.weight || 1);
                } else {
                    // Detailed rubric: iterate through criteria (check if criteria exists)
                    cat.criteria?.forEach((crit, critIndex) => { // Add null check
                        const scoreKey = `${catIndex}_${critIndex}`;
                        const scoreValue = Number(scoreDoc.scores?.[scoreKey] || 0);
                        reviewerRaw += scoreValue;
                        // Use criterion weight (default to 0 if missing)
                        reviewerWeighted += scoreValue * (crit.weight || 0);
                    });
                }
            });

            overallTotalRaw += reviewerRaw;
            overallTotalWeighted += reviewerWeighted;
            scoresByReviewer.push({
                reviewerId: scoreDoc.reviewerId,
                reviewerName: scoreDoc.reviewerName,
                raw: reviewerRaw,
                weighted: reviewerWeighted
            });
        });

        return {
            id: pkg.id, name: pkg.name, numReviewers,
            avgRawScore: overallTotalRaw / numReviewers,
            avgWeightedScore: overallTotalWeighted / numReviewers,
            totalRawScore: overallTotalRaw, // Sum across all reviewers
            totalWeightedScore: overallTotalWeighted, // Sum across all reviewers
            totalPossibleRaw, totalPossibleWeighted, // Add possible totals
            scoresByReviewer
        };
    });

    // Rank results based on average weighted score
    const rankedResults = [...results].sort((a, b) => b.avgWeightedScore - a.avgWeightedScore);
    let currentRank = 1;
    for (let i = 0; i < rankedResults.length; i++) {
        // Increment rank only if the score is strictly less than the previous one
        if (i > 0 && rankedResults[i].avgWeightedScore < rankedResults[i - 1].avgWeightedScore) {
            currentRank = i + 1;
        }
        rankedResults[i].rank = currentRank;
    }

    return rankedResults;
}